import { useEffect, useRef } from 'react'

import { bottomWhiteSpaceSmall } from '@/constants'
import { useQuery } from '@api/hooks'
import { ActionCenterTaskType } from '@domain'
import { Divider, Grid2, Skeleton, Stack, Tab, Tabs, Typography } from '@mui/material'
import ContractLoadingApi, { ContractLoadingKeys } from '@services/contractLoading.api'
import {
	ContractLoadingSummary,
	ContractSummary,
	ContractSummaryStatus,
} from '@services/contractLoading.types'
import { useQueryClient } from '@tanstack/react-query'
import { largeNumToString } from '@utils/stringHelpers'
import { updateContractLoadingActionCenterCache } from '@views/MarketBaskets/ActionCenter/ContractLoading/contractLoading.utils'
import { useParams } from 'react-router-dom'
import { StringParam, useQueryParam } from 'use-query-params'

import ContractCard from './ContractCard'

type Props = {
	onSelect: (id: Id) => void
	onChangeStatus: (status: ContractSummaryStatus | null) => void
	onClose: () => void
}

export default function ContractList({ onSelect, onChangeStatus, onClose }: Props) {
	const [selectedId, setSelectedId] = useQueryParam('contract', StringParam)
	const [activeTask] = useQueryParam('active-task', StringParam)
	const [statusFilter] = useQueryParam('contracts', StringParam)

	const { marketBasketId } = useParams()
	const queryClient = useQueryClient()

	const prevPendingRef = useRef<number | null>(null)

	const { data: summary, isLoading: isLoadingSummary } = useQuery({
		queryKey: ContractLoadingKeys.summary(marketBasketId),
		queryFn: () => ContractLoadingApi.summary(marketBasketId),
	})

	const { data: contractsForReview, isLoading: isLoadingItems } = useQuery({
		queryKey: ContractLoadingKeys.list(marketBasketId, {
			status: statusFilter as ContractSummaryStatus,
		}),
		queryFn: () =>
			ContractLoadingApi.list(marketBasketId, {
				status: statusFilter as ContractSummaryStatus,
			}),
	})

	// Closes drawer when pending contracts are completed
	useEffect(() => {
		if (!summary) return

		const completedPendingContracts =
			prevPendingRef.current !== null && prevPendingRef.current > 0 && summary.pending === 0

		if (completedPendingContracts) {
			updateContractLoadingActionCenterCache(queryClient, marketBasketId)
			onClose()
		}

		prevPendingRef.current = summary.pending
	}, [summary, onClose, queryClient, marketBasketId])

	// Auto-select first item when switching tabs
	useEffect(() => {
		if (activeTask !== ActionCenterTaskType.CONTRACT_LOADING) return
		if (selectedId) return
		const hasContracts = contractsForReview && contractsForReview.length > 0
		if (!hasContracts) return

		setSelectedId(contractsForReview[0].id.toString())
	}, [activeTask, contractsForReview, selectedId, setSelectedId])

	// Prefetch next contract when selectedId changes
	// handling in useEffect so that prefetch runs during initial load (auto selection of id)
	useEffect(() => {
		if (!contractsForReview || contractsForReview.length === 0) return

		const currentIndex = contractsForReview.findIndex((d) => d.id === Number(selectedId))
		const nextItem = contractsForReview[currentIndex + 1]
		if (!nextItem) return

		queryClient.prefetchQuery({
			queryKey: ContractLoadingKeys.contract(marketBasketId, nextItem.id, { matchType: 'ALL' }),
			queryFn: () => ContractLoadingApi.contract(nextItem.id, { matchType: 'ALL' }),
		})
	}, [selectedId, contractsForReview, queryClient, marketBasketId])

	return (
		<Stack
			sx={{ width: '48rem', height: '100%', overflow: 'hidden' }}
			data-testid="contract-loading-list"
		>
			<Typography variant="h6" mb={2} px={2} pt={2}>
				Contract Loading
			</Typography>

			<Divider />

			<SummaryUI summary={summary} isLoading={isLoadingSummary} />

			<Divider />

			<ListUI
				items={contractsForReview ?? []}
				isLoading={isLoadingItems}
				isLoadingSummary={isLoadingSummary}
				summary={summary}
				selectedId={selectedId}
				statusFilter={statusFilter as ContractSummaryStatus | null}
				onSelect={onSelect}
				onChangeStatus={onChangeStatus}
			/>
		</Stack>
	)
}

type SummaryUIProps = {
	summary: ContractLoadingSummary | undefined
	isLoading: boolean
}

export function SummaryUI({ summary, isLoading }: SummaryUIProps) {
	const completed = summary ? summary.accepted + summary.rejected : 0

	if (isLoading) {
		return (
			<Stack
				direction="row"
				spacing={2}
				sx={{ py: 1, px: 2 }}
				data-testid="contract-loading-summary-loading"
			>
				<Skeleton variant="rectangular" width="50%" height={60} />
				<Skeleton variant="rectangular" width="50%" height={60} />
			</Stack>
		)
	}

	return (
		<Grid2 container data-testid="contract-loading-summary">
			<Grid2 size={4} sx={{ px: 4, py: 2 }}>
				<Typography variant="body2" fontWeight="bold" color="text.secondary" pb={1}>
					Contracts
				</Typography>
				<Typography variant="h6">
					{completed}/{summary?.total ?? 0}
				</Typography>
				<Typography variant="body2" color="text.secondary">
					Completed
				</Typography>
			</Grid2>
			<Divider orientation="vertical" flexItem />
			<Grid2 size="grow" sx={{ px: 4, py: 2 }}>
				<Typography variant="body2" fontWeight="bold" color="text.secondary" pb={1}>
					Accepted Savings Opportunity
				</Typography>
				<Typography variant="h6">
					${largeNumToString(summary?.acceptedAnnualTotalSavings ?? 0)}
				</Typography>
			</Grid2>
		</Grid2>
	)
}

type ListUIProps = {
	items: ContractSummary[]
	isLoading: boolean
	isLoadingSummary: boolean
	summary: ContractLoadingSummary | undefined
	selectedId: Id | null
	statusFilter: ContractSummaryStatus | null
	onSelect: (id: Id) => void
	onChangeStatus: (status: ContractSummaryStatus | null) => void
}

export function ListUI({
	items,
	isLoading,
	isLoadingSummary,
	summary,
	selectedId,
	statusFilter,
	onSelect,
	onChangeStatus,
}: ListUIProps) {
	const hasNoItems = !isLoading && items.length === 0

	return (
		<Stack sx={{ px: 2, flex: 1, overflow: 'hidden' }}>
			<Tabs
				value={statusFilter ?? ContractSummaryStatus.PENDING}
				onChange={(_, value) => {
					onChangeStatus(value)
					onSelect(null)
				}}
			>
				<Tab
					label={`Review (${isLoadingSummary ? '--' : (summary?.pending ?? 0)})`}
					value={ContractSummaryStatus.PENDING}
				/>
				<Tab
					label={`Accepted (${isLoadingSummary ? '--' : (summary?.accepted ?? 0)})`}
					value={ContractSummaryStatus.ACCEPTED}
				/>
				<Tab
					label={`Rejected (${isLoadingSummary ? '--' : (summary?.rejected ?? 0)})`}
					value={ContractSummaryStatus.REJECTED}
				/>
				<Tab
					label={`All (${isLoadingSummary ? '--' : (summary?.total ?? 0)})`}
					value={ContractSummaryStatus.ALL}
				/>
			</Tabs>
			<Stack
				spacing={2}
				sx={{ overflowY: 'auto', mt: 2, flex: 1, mr: -2, pr: 2, pb: bottomWhiteSpaceSmall }}
			>
				{isLoading && (
					<>
						<Skeleton variant="rectangular" width="100%" height={100} />
						<Skeleton variant="rectangular" width="100%" height={100} />
						<Skeleton variant="rectangular" width="100%" height={100} />
						<Skeleton variant="rectangular" width="100%" height={100} />
					</>
				)}

				{hasNoItems && (
					<Typography variant="body2" color="text.secondary" textAlign="center" mt={4}>
						No {statusFilter ? statusFilter.toLowerCase() : ''} Contracts for Review
					</Typography>
				)}

				{!isLoading &&
					items.map((item) => (
						<ContractCard
							key={item.id}
							id={item.id}
							selected={item.id === Number(selectedId)}
							termSetName={item.termSetName}
							topLevelCustomerName={item.topLevelCustomerName}
							manufacturerName={item.manufacturerName}
							recommended={item.recommend}
							onContract={item.onContract}
							requiresPriceImpl={item.requiresPriceImplementation}
							directDealRejected={item.directDealRejected}
							numProducts={item.numProducts}
							annualTotalSavings={item.annualTotalSavings}
							onClick={() => onSelect(item.id)}
						/>
					))}
			</Stack>
		</Stack>
	)
}
